import Navbar from "@/components/navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, Eye, Lock, Users, Database, FileText } from "lucide-react";

export default function PrivacyPolicy() {
  const lastUpdated = "January 2025";

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 pt-24">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-neutral-900 mb-4">Privacy Policy</h1>
          <p className="text-xl text-neutral-600">
            How we collect, use, and protect your information on The MAX Platform™
          </p>
          <p className="text-sm text-neutral-500 mt-4">
            Last updated: {lastUpdated}
          </p>
        </div>

        <div className="space-y-8">
          {/* Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-max-brand" />
                Privacy Overview
              </CardTitle>
            </CardHeader>
            <CardContent className="prose max-w-none">
              <p>
                At MAX (Music Audience Exchange), we are committed to protecting your privacy and ensuring 
                the security of your personal information. This Privacy Policy explains how we collect, use, 
                disclose, and safeguard your information when you use The MAX Platform™.
              </p>
              <p>
                By using our platform, you agree to the collection and use of information in accordance 
                with this policy. We will not use or share your information except as described in this 
                Privacy Policy.
              </p>
            </CardContent>
          </Card>

          {/* Information We Collect */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-max-brand" />
                Information We Collect
              </CardTitle>
            </CardHeader>
            <CardContent className="prose max-w-none">
              <h4>Personal Information</h4>
              <ul>
                <li>Name, email address, and contact information</li>
                <li>Company information and job title</li>
                <li>Profile photos and company logos</li>
                <li>Account credentials and authentication data</li>
              </ul>

              <h4>Platform Usage Data</h4>
              <ul>
                <li>Search queries and filter preferences</li>
                <li>Shows saved and proposals created</li>
                <li>Platform navigation and feature usage</li>
                <li>Session duration and interaction patterns</li>
              </ul>

              <h4>Technical Information</h4>
              <ul>
                <li>IP address and device information</li>
                <li>Browser type and operating system</li>
                <li>Cookies and similar tracking technologies</li>
                <li>Performance and error logs</li>
              </ul>
            </CardContent>
          </Card>

          {/* How We Use Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-max-brand" />
                How We Use Your Information
              </CardTitle>
            </CardHeader>
            <CardContent className="prose max-w-none">
              <h4>Platform Functionality</h4>
              <ul>
                <li>Provide and maintain The MAX Platform™ services</li>
                <li>Process and fulfill sponsorship proposals</li>
                <li>Personalize your experience and recommendations</li>
                <li>Enable communication between users</li>
              </ul>

              <h4>Business Operations</h4>
              <ul>
                <li>Analyze platform usage to improve services</li>
                <li>Generate anonymized industry insights</li>
                <li>Provide customer support and technical assistance</li>
                <li>Send important platform updates and notifications</li>
              </ul>

              <h4>Legal and Security</h4>
              <ul>
                <li>Comply with legal obligations and regulations</li>
                <li>Protect against fraud and security threats</li>
                <li>Enforce our Terms of Service</li>
                <li>Resolve disputes and legal claims</li>
              </ul>
            </CardContent>
          </Card>

          {/* Data Sharing */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-max-brand" />
                How We Share Information
              </CardTitle>
            </CardHeader>
            <CardContent className="prose max-w-none">
              <h4>Service Providers</h4>
              <p>
                We may share your information with trusted third-party service providers who assist 
                us in operating the platform, conducting business, or serving our users. These 
                providers are bound by confidentiality agreements.
              </p>

              <h4>Business Partners</h4>
              <p>
                With your explicit consent, we may share relevant information with artists, venues, 
                or other business partners to facilitate sponsorship opportunities and partnerships.
              </p>

              <h4>Legal Requirements</h4>
              <p>
                We may disclose your information if required by law, regulation, legal process, or 
                governmental request, or to protect the rights, property, or safety of MAX, our 
                users, or others.
              </p>

              <h4>Business Transfers</h4>
              <p>
                In the event of a merger, acquisition, or sale of assets, your information may be 
                transferred as part of the business transaction, subject to appropriate protections.
              </p>
            </CardContent>
          </Card>

          {/* Data Security */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-max-brand" />
                Data Security
              </CardTitle>
            </CardHeader>
            <CardContent className="prose max-w-none">
              <p>
                We implement industry-standard security measures to protect your personal information 
                against unauthorized access, alteration, disclosure, or destruction. Our security 
                measures include:
              </p>
              <ul>
                <li>Encryption of data in transit and at rest</li>
                <li>Secure authentication and authorization systems</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Employee training on data protection practices</li>
                <li>Incident response and breach notification procedures</li>
              </ul>
              <p>
                While we strive to protect your information, no method of transmission over the internet 
                or electronic storage is 100% secure. We cannot guarantee absolute security but will 
                notify you promptly of any known security breaches.
              </p>
            </CardContent>
          </Card>

          {/* Your Rights */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-max-brand" />
                Your Privacy Rights
              </CardTitle>
            </CardHeader>
            <CardContent className="prose max-w-none">
              <p>You have the following rights regarding your personal information:</p>
              <ul>
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
                <li><strong>Portability:</strong> Request a machine-readable copy of your data</li>
                <li><strong>Restriction:</strong> Request limitation of processing in certain circumstances</li>
                <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
              </ul>
              <p>
                To exercise any of these rights, please contact us at privacy@max.live. We will respond 
                to your request within 30 days.
              </p>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Us</CardTitle>
            </CardHeader>
            <CardContent>
              <p>
                If you have any questions about this Privacy Policy or our privacy practices, 
                please contact us:
              </p>
              <div className="mt-4 space-y-2">
                <p><strong>Email:</strong> privacy@max.live</p>
                <p><strong>Address:</strong> Music Audience Exchange, Los Angeles, CA, United States</p>
                <p><strong>Data Protection Officer:</strong> dpo@max.live</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}